import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useUpdatePrompt, type PlainPrompt } from "@/queries/prompt-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC } from "react";

type Props = {
    skill: PlainPrompt;
    onClose: () => void;
};

export const EditSkillModal: FC<Props> = ({ skill, onClose }) => {
    const [name, setName] = useState(skill.metadata?.name || "");
    const [trigger, setTrigger] = useState(skill.spec?.command || "");
    const [description, setDescription] = useState(skill.metadata?.description || "");
    const [prompt, setPrompt] = useState(skill.spec?.prompt || "");

    const updatePrompt = useUpdatePrompt();
    const { toast } = useToast();

    const handleSave = useCallback(async () => {
        try {
            await updatePrompt.mutateAsync({
                promptId: skill.id,
                metadata: {
                    name: name.trim(),
                    description: description.trim(),
                },
                spec: {
                    prompt: prompt.trim(),
                    command: trigger.trim(),
                },
            });
            toast({
                title: "Skill updated successfully",
                description: `/${trigger} has been updated`,
            });
            onClose();
        } catch (error) {
            console.error(error);
            toast({
                title: "Failed to update skill",
                description: formatError(error),
            });
        }
    }, [skill.id, name, trigger, description, prompt, updatePrompt, toast, onClose]);

    const isValid = name.trim() && trigger.trim() && description.trim() && prompt.trim();

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="edit-skill-modal"
                data-track-location={TrackLocations.EditSkillModal}
            >
                <DialogHeader>
                    <DialogTitle>Edit Agent Skill</DialogTitle>
                    <DialogDescription>Update the skill details</DialogDescription>
                </DialogHeader>

                <DialogBody className="space-y-4">
                    <InputField id="edit-skill-name" label="Name" hint="The name of the skill">
                        <Input
                            id="edit-skill-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Skill name"
                            data-testid="edit-skill-name-input"
                        />
                    </InputField>

                    <InputField
                        id="edit-skill-trigger"
                        label="Slash Trigger"
                        hint="The trigger that will invoke this skill"
                    >
                        <Input
                            id="edit-skill-trigger"
                            value={trigger}
                            onChange={(e) => setTrigger(e.target.value)}
                            placeholder="trigger-word"
                            data-testid="edit-skill-trigger-input"
                            prefix="/"
                        />
                    </InputField>

                    <InputField
                        id="edit-skill-description"
                        label="Description"
                        hint="Brief description of what this skill does"
                    >
                        <Input
                            id="edit-skill-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Brief description of what this skill does"
                            data-testid="edit-skill-description-input"
                        />
                    </InputField>

                    <InputField
                        id="edit-skill-prompt"
                        label="Prompt"
                        hint={`The prompt that will be sent to the AI agent (${prompt.length.toLocaleString()} / ${(20000).toLocaleString()} characters)`}
                    >
                        <Textarea
                            id="edit-skill-prompt"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="The prompt that will be sent to the AI agent"
                            className="resize-none"
                            minHeight={120}
                            maxHeight={300}
                            data-testid="edit-skill-prompt-textarea"
                            maxLength={20000}
                        />
                    </InputField>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button
                        onClick={handleSave}
                        disabled={!isValid}
                        loading={updatePrompt.isPending}
                        data-testid="update-skill-button"
                        data-tracking-id="update-agent-skill-edit-skill-modal"
                    >
                        Update Skill
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
